import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Palette,
  Check,
  Lock
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';

const ThemeSelector: React.FC = () => {
  const { currentTheme, themes, setTheme, loading } = useTheme();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSelectTheme = async (slug: string) => {
    try {
      setSaving(true);
      setError('');
      await setTheme(slug);
      setSuccess('Theme applied successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to apply theme');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, maxWidth: 700, mx: 'auto' }}>
      <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Palette color="primary" />
        Theme Settings
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose a color theme for your dashboard. Premium themes are marked with a ⭐.
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
        {themes.map((theme) => (
          <Box
            key={theme.slug}
            onClick={() => !saving && handleSelectTheme(theme.slug)}
            sx={{
              border: currentTheme?.slug === theme.slug ? '3px solid' : '1px solid',
              borderColor: currentTheme?.slug === theme.slug ? 'primary.main' : 'divider',
              borderRadius: 2,
              p: 2,
              cursor: saving ? 'wait' : 'pointer',
              transition: 'all 0.2s',
              position: 'relative',
              '&:hover': {
                borderColor: 'primary.main',
                transform: 'translateY(-2px)',
                boxShadow: 2
              }
            }}
          >
            {/* Color Preview */}
            <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: theme.primary_color,
                  border: '2px solid white',
                  boxShadow: 1
                }}
              />
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  bgcolor: theme.secondary_color,
                  border: '2px solid white',
                  boxShadow: 1
                }}
              />
              {theme.accent_color && (
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: theme.accent_color,
                    border: '2px solid white',
                    boxShadow: 1
                  }}
                />
              )}
            </Box>

            {/* Theme Name */}
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {theme.name}
            </Typography>

            {/* Premium Badge */}
            {theme.is_premium && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <Lock sx={{ fontSize: 12, color: 'warning.main' }} />
                <Typography variant="caption" color="warning.main">
                  Premium
                </Typography>
              </Box>
            )}

            {/* Selected Indicator */}
            {currentTheme?.slug === theme.slug && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  bgcolor: 'primary.main',
                  borderRadius: '50%',
                  width: 24,
                  height: 24,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Check sx={{ fontSize: 16, color: 'white' }} />
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {/* Info Box */}
      <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1, mt: 3 }}>
        <Typography variant="body2" color="info.dark">
          <strong>Note:</strong> Premium themes (⭐) require admin approval. 
          Contact your administrator to unlock premium themes for your account.
        </Typography>
      </Box>
    </Paper>
  );
};

export default ThemeSelector;
